import { useEffect, useMemo } from 'react';

import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import { useSearchParams, useLocation } from 'react-router-dom';

import { Breadcrumbs } from '#client/components/Breadcrumbs';
import { DisableLayer } from '#client/components/DisableLayer';
import { FocusGrid, HubContainerGridStyled, HubMainWrapperStyled } from '#client/components/Grid';
import Notifications from '#client/components/Notifications';
import PayeeContainer from '#client/components/PayeesModal/components/PayeeContainer';
import { COUNTERPARTY_ID, PLAID_CALLBACK } from '#client/constants/modals';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import { deleteMultipleSearchParams } from '#client/utils/sanitize';

import SharedNavigateModals from '../SharedNavigateModals';
import { BreadcrumbWrapStyled } from '../SharedStyles';

const { isWebView } = WebView;

export default function Payees() {
  const { pathname } = useLocation();
  const { isOpeningModal } = useCheckOpenModal();
  const { isDisable } = useDisableDashboardContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const properties = useMemo(
    () => ({
      customDestinationPageName_: 'payees_screen',
      appPageName: 'active_banking_dashboard',
      appSection: 'banking',
      experienceOrigin: 'dashboard'
    }),
    []
  );

  useEffect(() => {
    // Delete specific search parameters from the URL
    // It belongs to the callback on webview then link Plaid successfully
    const counterpartyId = searchParams.get(COUNTERPARTY_ID);
    const plaidCallback = searchParams.get(PLAID_CALLBACK);
    if (counterpartyId && plaidCallback) {
      const newSearchParams = deleteMultipleSearchParams(
        [PLAID_CALLBACK, COUNTERPARTY_ID],
        searchParams
      );
      setSearchParams(newSearchParams, { replace: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isOpeningModal ? (
    <SharedNavigateModals />
  ) : (
    <HubContainerGridStyled>
      <FocusGrid>
        <HubMainWrapperStyled>
          {!isWebView && (
            <BreadcrumbWrapStyled>
              <Breadcrumbs
                items={[
                  {
                    to: '../../finances',
                    label: 'Finances'
                  },
                  {
                    to: '../dashboard',
                    label: 'Banking'
                  },
                  {
                    label: 'Payees'
                  }
                ]}
              />
            </BreadcrumbWrapStyled>
          )}
          <Notifications />
          <PageViewedTracker
            {...properties}
            key={pathname}
          />
          <Box position="relative">
            {isDisable && <DisableLayer aria-label="Disable layer" />}
            <PayeeContainer />
          </Box>
        </HubMainWrapperStyled>
      </FocusGrid>
    </HubContainerGridStyled>
  );
}
